import { useEffect, useState, useCallback } from "react";
import { useApp } from "@/context/AppContext";
import api, { apiError, fileUrl } from "@/lib/api";
import { Reveal } from "@/components/Reveal";
import { toast } from "sonner";
import { Upload, Trash2, Loader2, Plus, Play, MapPin, ImageIcon, Pencil, Check, X } from "lucide-react";

/* Admin-manageable photo/video showcase for a page section.
   Reuses /api/media (category = `category`). Admins (edit mode) can add + delete. */
export default function ProjectShowcase({ category, title, subtitle, eyebrow, accent = "amber" }) {
  const { user, editMode } = useApp();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ title: "", location: "" });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editVals, setEditVals] = useState({ title: "", location: "" });

  const canManage = user && editMode;

  const load = useCallback(() => {
    api.get(`/media?category=${category}`).then(({ data }) => setItems(data)).catch((e) => console.error(e)).finally(() => setLoading(false));
  }, [category]);
  useEffect(() => { load(); }, [load]);

  const upload = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Please choose a photo or video");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("title", form.title || file.name);
      fd.append("location", form.location);
      fd.append("category", category);
      fd.append("media_type", file.type.startsWith("video") ? "video" : "photo");
      await api.post("/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Project media added");
      setForm({ title: "", location: "" }); setFile(null); setShowForm(false);
      load();
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    try { await api.delete(`/media/${id}`); toast.success("Removed"); setItems((x) => x.filter((i) => i.id !== id)); }
    catch (err) { toast.error(apiError(err)); }
  };

  const saveEdit = async (id) => {
    try {
      await api.patch(`/media/${id}`, editVals);
      setItems((x) => x.map((i) => i.id === id ? { ...i, ...editVals } : i));
      setEditId(null); toast.success("Updated");
    } catch (err) { toast.error(apiError(err)); }
  };

  if (!canManage && items.length === 0) return null;

  return (
    <section className="oj-section" data-testid={`showcase-${category}`}>
      <div className="oj-container">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
          <div className="max-w-2xl">
            {eyebrow && <div className="oj-eyebrow mb-3">{eyebrow}</div>}
            <h2 className="oj-h2">{title}</h2>
            {subtitle && <p className="mt-3 text-slate-600 leading-relaxed">{subtitle}</p>}
          </div>
          {canManage && (
            <button onClick={() => setShowForm((s) => !s)} className="oj-btn-gold !py-2.5 !px-5 !text-sm" data-testid={`showcase-add-toggle-${category}`}>
              <Plus className="w-4 h-4" /> Add Project Media
            </button>
          )}
        </div>

        {canManage && showForm && (
          <Reveal>
            <form onSubmit={upload} className="oj-card p-6 mb-8" data-testid={`showcase-form-${category}`}>
              <h3 className="oj-h3 mb-4">Upload Photo / Video</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <input placeholder="Project title / caption" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="oj-input" data-testid={`showcase-title-${category}`} />
                <input placeholder="Location (optional)" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="oj-input" data-testid={`showcase-location-${category}`} />
                <label className="flex items-center gap-3 oj-input cursor-pointer sm:col-span-2">
                  <Upload className="w-5 h-5 text-teal-600 shrink-0" />
                  <span className="text-sm text-slate-600 truncate">{file ? file.name : "Choose image or video file"}</span>
                  <input type="file" accept="image/*,video/*" className="hidden" onChange={(e) => setFile(e.target.files[0])} data-testid={`showcase-file-${category}`} />
                </label>
              </div>
              <div className="flex gap-3 mt-5">
                <button disabled={busy} className="oj-btn-gold !py-2.5 !px-5 !text-sm" data-testid={`showcase-upload-${category}`}>{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Upload</button>
                <button type="button" onClick={() => { setShowForm(false); setFile(null); }} className="oj-btn-ghost !py-2.5 !px-5 !text-sm">Cancel</button>
              </div>
            </form>
          </Reveal>
        )}

        {loading ? (
          <div className="grid place-items-center py-16 text-teal-600"><Loader2 className="w-8 h-8 animate-spin" /></div>
        ) : items.length === 0 ? (
          <div className="oj-card p-10 text-center text-slate-500"><ImageIcon className="w-10 h-10 mx-auto mb-3 opacity-50" />No project media yet. Use "Add Project Media" to upload photos or videos.</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {items.map((m, i) => {
              const isVideo = m.media_type === "video" || m.content_type?.startsWith("video");
              const src = fileUrl(m.storage_path);
              return (
                <Reveal key={m.id} delay={(i % 3) * 0.06}>
                  <div className="group oj-card overflow-hidden h-full" data-testid={`showcase-item-${m.id}`}>
                    <div className="relative h-56 bg-teal-950/5">
                      {isVideo ? (
                        <video src={src} className="w-full h-full object-cover" controls playsInline preload="metadata" />
                      ) : (
                        <img src={src} alt={m.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                      )}
                      {isVideo && !canManage && (
                        <div className="absolute inset-0 grid place-items-center pointer-events-none">
                          <div className="grid place-items-center w-12 h-12 rounded-full bg-[#017471]/80 border border-white/40"><Play className="w-5 h-5 text-white ml-0.5" fill="white" /></div>
                        </div>
                      )}
                      {canManage && (
                        <button onClick={() => remove(m.id)} data-testid={`showcase-delete-${m.id}`} className="absolute top-3 right-3 grid place-items-center w-9 h-9 rounded-lg bg-red-600 text-white shadow-lg z-10 hover:bg-red-700"><Trash2 className="w-4 h-4" /></button>
                      )}
                    </div>
                    <div className="p-5">
                      {editId === m.id ? (
                        <div className="space-y-2">
                          <input value={editVals.title} onChange={(e) => setEditVals({ ...editVals, title: e.target.value })} className="oj-input !py-2 !text-sm" data-testid={`showcase-edit-title-${m.id}`} />
                          <input value={editVals.location} onChange={(e) => setEditVals({ ...editVals, location: e.target.value })} placeholder="Location" className="oj-input !py-2 !text-sm" />
                          <div className="flex gap-2">
                            <button onClick={() => saveEdit(m.id)} className="oj-btn-gold !py-1.5 !px-3 !text-xs" data-testid={`showcase-save-${m.id}`}><Check className="w-3.5 h-3.5" /> Save</button>
                            <button onClick={() => setEditId(null)} className="oj-btn-ghost !py-1.5 !px-3 !text-xs"><X className="w-3.5 h-3.5" /></button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="flex items-start justify-between gap-2">
                            <h4 className="font-bold text-[#0B3B39] leading-snug">{m.title}</h4>
                            {canManage && <button onClick={() => { setEditId(m.id); setEditVals({ title: m.title || "", location: m.location || "" }); }} data-testid={`showcase-edit-${m.id}`} className="text-teal-600 hover:text-amber-600 shrink-0"><Pencil className="w-4 h-4" /></button>}
                          </div>
                          {m.location && <div className="mt-2 flex items-center gap-1.5 text-xs text-slate-500"><MapPin className="w-3.5 h-3.5 text-teal-500" /> {m.location}</div>}
                        </>
                      )}
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}
